import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, PHONE, IMAGES, coverSrc } from "../lib/api";
import { useSEO } from "../lib/useSEO";
import { Reveal } from "../components/Reveal";
import { Breadcrumbs, CtaBand, FaqAccordion } from "../components/Shared";
import { CallButton } from "../components/Layout";
import ArticlesTeaser from "../components/ArticlesTeaser";
import { CalendarDays } from "lucide-react";

const fmtDate = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

export default function Article() {
  const { slug } = useParams();
  const [a, setA] = useState(null);
  useEffect(() => { window.scrollTo(0, 0); api.get(`/articles/${slug}`).then((r) => setA(r.data)).catch(() => setA(false)); }, [slug]);

  useSEO({
    title: a ? `${a.title} | Serrurier Seine-et-Marne 77 ☎ ${PHONE}` : "Article",
    description: a ? `${a.meta_description} Serrurier d'urgence 7j/7 dans le 77 ☎ ${PHONE}.` : "",
    path: `/conseils/${slug}`,
    faq: a && a.faq && a.faq.length ? a.faq : null,
    keywords: a ? (a.keywords || ["serrurier Seine-et-Marne", "serrurier 77", "dépannage serrurier 77", a.category]) : null,
  });

  if (a === false) return <div className="pt-48 pb-32 text-center bg-white"><h1 className="text-4xl font-display text-zinc-900">Article introuvable</h1><Link to="/conseils" className="text-primary mt-4 inline-block">← Tous les articles</Link></div>;
  if (!a) return <div className="pt-40 pb-40 text-center text-zinc-400 bg-white">Chargement…</div>;

  return (
    <div className="bg-white">
      <section className="relative pt-32 pb-10 sm:pt-36">
        <div className="max-w-3xl mx-auto px-6">
          <Breadcrumbs items={[{ label: "Accueil", to: "/" }, { label: "Conseils", to: "/conseils" }, { label: a.title }]} />
          <Reveal>
            <span className="inline-block rounded-full bg-primary/10 px-4 py-1.5 text-sm font-medium text-primary mb-5">{a.category}</span>
            <h1 className="text-4xl sm:text-5xl font-display font-semibold tracking-tighter leading-[1.0] text-zinc-900">{a.title}</h1>
            <div className="flex items-center gap-1.5 text-sm text-zinc-400 mt-5"><CalendarDays className="h-4 w-4" /> {fmtDate(a.date)}{a.updated_at ? ` · Mis à jour le ${fmtDate(a.updated_at)}` : ""}</div>
          </Reveal>
        </div>
      </section>

      <Reveal className="max-w-4xl mx-auto px-6">
        <div className="rounded-3xl overflow-hidden border border-zinc-200 shadow-lg">
          <img src={coverSrc(a)} alt={a.title} className="w-full h-64 sm:h-96 object-cover" />
        </div>
      </Reveal>

      <article className="max-w-3xl mx-auto px-6 py-16">
        {a.sections.map((s, i) => (
          <Reveal key={i} className="mb-12">
            <h2 className="text-2xl sm:text-3xl font-display font-medium tracking-tight mb-5 text-zinc-900">{s.h2}</h2>
            {s.paragraphs.map((p, j) => <p key={j} className="text-zinc-600 leading-relaxed mb-5 text-[1.05rem]">{p}</p>)}
          </Reveal>
        ))}
        {a.internal_links && a.internal_links.length > 0 && (
          <Reveal className="mt-10 rounded-2xl border border-zinc-200 p-6">
            <p className="text-sm font-medium text-zinc-900 mb-3">À découvrir aussi</p>
            <div className="flex flex-wrap gap-2.5">
              {a.internal_links.map((l, i) => (
                <Link key={i} to={l.path} className="rounded-full border border-zinc-200 bg-white px-4 py-2 text-sm text-zinc-700 hover:text-primary hover:border-primary transition-colors">{l.anchor}</Link>
              ))}
            </div>
          </Reveal>
        )}
        <Reveal className="mt-12 rounded-2xl bg-zinc-50 border border-zinc-200 p-8 text-center">
          <p className="text-lg font-medium text-zinc-900 mb-2">{a.cta || "Une question ou une urgence serrurerie ?"}</p>
          <p className="text-zinc-500 mb-6">Nos serruriers interviennent 7j/7 dans toute la Seine-et-Marne. Devis gratuit.</p>
          <CallButton className="px-7 py-4 text-base justify-center" testid="article-call" />
        </Reveal>
      </article>

      {a.faq && a.faq.length > 0 && <FaqAccordion items={a.faq} title="Questions fréquentes" />}
      <ArticlesTeaser title="À lire aussi" className="bg-zinc-50" />
      <CtaBand />
    </div>
  );
}
